import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCopilot } from "@/context/CopilotContext";
import { useGuidedTour } from "@/context/GuidedTourContext";
import { Button } from "@/components/ui/button";
import { Sparkles, X, Send, Loader2, CheckCircle2, FileText, StickyNote, CalendarPlus, ArrowRight, Compass, Pencil, MonitorPlay, Play, RotateCcw } from "lucide-react";
import { RenameCopilotDialog } from "@/components/CopilotNaming";

function useRouteEntity() {
  const loc = useLocation();
  const m = loc.pathname.match(/\/(leads?|clients?|cases?|pipeline)\/([\w-]{6,})/);
  return m ? m[2] : null;
}

export function Copilot() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { activeEntity } = useCopilot();
  const gt = useGuidedTour();
  const loc = useLocation();
  const navigate = useNavigate();
  const routeEntity = useRouteEntity();
  const entityId = activeEntity?.id || routeEntity;
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [tour, setTour] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const endRef = useRef(null);
  const lang = i18n.language === "es" ? "es" : "en";
  const assistantName = user?.assistant_name || "";

  const loadCtx = useCallback(() => {
    api.get(`/copilot/context?page=${encodeURIComponent(loc.pathname)}`)
      .then(({ data }) => setCtx(data)).catch(() => setCtx(null));
  }, [loc.pathname]);
  useEffect(() => { if (open) loadCtx(); }, [open, loadCtx]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, pending, tour]);

  if (!user || user.role === "vendor") return null;
  const canManage = ["admin", "ops_manager"].includes(user.role);

  const push = (m) => setMsgs((x) => [...x.slice(-11), m]);

  const startTour = () => {
    if (!assistantName) return;
    if (user.role === "agent") {
      setTour(null);
      setOpen(false);
      if (loc.pathname !== "/") navigate("/");
      window.setTimeout(() => window.dispatchEvent(new CustomEvent("fortunate:start-agent-tour")), loc.pathname === "/" ? 0 : 180);
      return;
    }
    const steps = ctx?.tour || [];
    if (!steps.length) return;
    setTour({ steps, i: 0 });
  };
  const endTour = () => setTour(null);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    if (ctx?.tour_label && (q === ctx.tour_label || q === t("copilot.take_tour"))) {
      setInput(""); startTour(); return;
    }
    push({ role: "user", text: q });
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/copilot/chat", { message: q, page: loc.pathname, lang, entity_id: entityId });
      push({ role: "assistant", text: data.reply, nav: data.nav, category: data.category });
    } catch (e) {
      push({ role: "assistant", text: t("copilot.unavailable"), category: "error" });
    } finally { setBusy(false); }
  };

  const summarize = async () => {
    if (!entityId || busy) return;
    setBusy(true); push({ role: "user", text: t("copilot.summarize") });
    try {
      const { data } = await api.post("/copilot/summary", { entity_type: "lead", entity_id: entityId, lang });
      push({ role: "assistant", text: data.summary, category: "summary", redaction: data.redaction_applied });
    } catch (e) {
      push({ role: "assistant", text: t("copilot.unavailable"), category: "error" });
    } finally { setBusy(false); }
  };

  const draft = async (kind) => {
    setBusy(true); push({ role: "user", text: t("copilot.draft_followup") });
    try {
      const { data } = await api.post("/copilot/draft", { kind, entity_id: entityId, lang });
      push({ role: "assistant", text: data.draft, category: "draft" });
    } catch (e) {
      push({ role: "assistant", text: t("copilot.unavailable"), category: "error" });
    } finally { setBusy(false); }
  };

  const proposeAction = async (action, params) => {
    if (!entityId) { push({ role: "assistant", text: t("copilot.missing_info"), category: "error" }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/action", { action, entity_id: entityId, confirmed: false, ...params });
      setPending({ action, params, preview: data.preview });
    } catch (e) {
      push({ role: "assistant", text: formatApiErrorDetail(e?.response?.data?.detail) || t("common.unauthorized"), category: "error" });
    } finally { setBusy(false); }
  };
  const confirmAction = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/action", { action: pending.action, entity_id: entityId, confirmed: true, ...pending.params });
      push({ role: "assistant", text: `✓ ${t("copilot.action_done")}`, category: "action_done", result: data.result });
    } catch (e) {
      push({ role: "assistant", text: formatApiErrorDetail(e?.response?.data?.detail) || t("common.error"), category: "error" });
    } finally { setPending(null); setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="copilot-open"
          aria-label={t("copilot.title")} title={t("copilot.title")}
          className="fixed bottom-5 right-5 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-[#17324D] text-white shadow-lg transition-transform hover:scale-105">
          <Sparkles className="h-5 w-5 text-[#B28A3D]" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-[60] flex h-[560px] w-[380px] max-w-[92vw] flex-col overflow-hidden rounded-xl border bg-card shadow-2xl" data-testid="copilot-panel">
          <div className="flex items-center justify-between border-b bg-[#17324D] px-4 py-3 text-white">
            <div className="flex min-w-0 items-center gap-2 text-sm font-semibold" data-testid="copilot-identity-header">
              <Sparkles className="h-4 w-4 shrink-0 text-[#B28A3D]" />
              <span className="truncate">{assistantName ? `${assistantName} · ${t("copilot_identity.product")}` : t("copilot.title")}</span>
              <button onClick={() => setRenameOpen(true)} data-testid="copilot-rename-btn" title={t("copilot_identity.rename")}
                className="rounded p-1 text-slate-300 hover:bg-white/10 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
            </div>
            <div className="flex items-center gap-1">
              {ctx?.tour?.length > 0 && (
                <button onClick={() => (tour ? endTour() : startTour())} disabled={!assistantName}
                  data-testid="copilot-tour-btn" title={!assistantName ? t("copilot_identity.tour_locked") : ""}
                  className="flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-white/10 disabled:opacity-40">
                  <Compass className="h-3.5 w-3.5 text-[#B28A3D]" /> {user.role === "agent" ? t("copilot.take_tour") : (tour ? t("copilot.end_tour") : t("copilot.take_tour"))}
                </button>
              )}
              <button onClick={() => setOpen(false)} data-testid="copilot-close" className="rounded p-1 hover:bg-white/10"><X className="h-4 w-4" /></button>
            </div>
          </div>

          <RenameCopilotDialog open={renameOpen} onClose={() => setRenameOpen(false)} />

          <div className="flex-1 space-y-3 overflow-y-auto p-3 kanban-scroll">
            {ctx && msgs.length === 0 && (
              <div className="rounded-lg bg-muted/60 p-3 text-xs">
                <div className="font-semibold">{ctx.title}</div>
                <div className="mt-1 text-muted-foreground">{ctx.help}</div>
              </div>
            )}
            {assistantName && msgs.length === 0 && gt?.session && user.role !== "agent" && (
              <PresentationEntry gt={gt} onLaunch={() => setOpen(false)} />
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`copilot-msg-${m.role}`}
                  className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-xs ${m.role === "user" ? "bg-[#17324D] text-white" : m.category === "error" ? "bg-red-50 text-red-700" : "bg-muted"}`}>
                  {m.category === "draft" && <div className="mb-1 text-[10px] font-semibold uppercase text-[#B28A3D]">{t("copilot.draft_label")}</div>}
                  {m.category === "action_done" && <CheckCircle2 className="mb-1 inline h-3.5 w-3.5 text-green-600" />}
                  {m.text}
                  {m.redaction && <div className="mt-1 text-[10px] text-muted-foreground">🔒 restricted values excluded</div>}
                  {m.nav && (
                    <button onClick={() => { navigate(m.nav); }} data-testid="copilot-nav-btn"
                      className="mt-2 flex items-center gap-1 text-[11px] font-medium text-[#17324D] underline">
                      {m.nav} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("copilot.thinking")}</div>}

            {tour && user.role !== "agent" && (
              <div className="rounded-lg border-2 border-[#17324D] bg-slate-50 p-3 text-xs" data-testid="copilot-tour-card">
                <div className="flex items-center gap-1.5 font-semibold text-[#17324D]"><Compass className="h-3.5 w-3.5 text-[#B28A3D]" /> {ctx?.title}</div>
                {tour.i === 0 && <div className="mt-1 text-muted-foreground">{t("copilot.tour_intro")}</div>}
                <div className="mt-2 rounded bg-white p-2" data-testid="copilot-tour-step">{tour.steps[tour.i]}</div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground" data-testid="copilot-tour-progress">{t("copilot.tour_step", { current: tour.i + 1, total: tour.steps.length })}</span>
                  <div className="flex gap-2">
                    {tour.i + 1 < tour.steps.length
                      ? <Button size="sm" onClick={() => setTour((tt) => ({ ...tt, i: tt.i + 1 }))} data-testid="copilot-tour-next">{t("copilot.tour_next")}</Button>
                      : <Button size="sm" onClick={endTour} data-testid="copilot-tour-finish">{t("copilot.tour_done")}</Button>}
                  </div>
                </div>
              </div>
            )}

            {pending && (
              <div className="rounded-lg border-2 border-[#B28A3D] bg-amber-50 p-3 text-xs" data-testid="copilot-confirm-card">
                <div className="font-semibold">{t("copilot.confirm_title")}</div>
                <div className="mt-1 text-muted-foreground">{t("copilot.confirm_body")}</div>
                <div className="mt-2 rounded bg-white p-2">{pending.preview}</div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={confirmAction} disabled={busy} data-testid="copilot-confirm-btn">{t("common.confirm")}</Button>
                  <Button size="sm" variant="outline" onClick={() => setPending(null)} data-testid="copilot-cancel-btn">{t("common.cancel")}</Button>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t p-2">
            <div className="mb-2 flex flex-wrap gap-1">
              {entityId && <>
                <Chip onClick={summarize} icon={FileText} label={t("copilot.summarize")} testid="copilot-summarize" />
                <Chip onClick={() => draft("followup_sms")} icon={FileText} label={t("copilot.draft_followup")} testid="copilot-draft" />
                <Chip onClick={() => { const txt = prompt(t("copilot.note_prompt")); if (txt) proposeAction("add_note", { text: txt }); }} icon={StickyNote} label={t("copilot.add_note")} testid="copilot-addnote" />
                <Chip onClick={() => { const txt = prompt(t("copilot.followup_prompt")); if (txt !== null) proposeAction("create_followup", { text: txt, followup_type: "Call" }); }} icon={CalendarPlus} label={t("copilot.create_followup")} testid="copilot-createfu" />
              </>}
              {!entityId && ctx?.suggestions?.map((s, i) => (
                <Chip key={i} onClick={() => send(s)} label={s} testid={`copilot-sugg-${i}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t("copilot.placeholder")} data-testid="copilot-input"
                className="flex-1 rounded-md border px-3 py-2 text-xs outline-none" />
              <Button size="sm" onClick={() => send()} disabled={busy || !input.trim()} data-testid="copilot-send"><Send className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Chip({ onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="flex items-center gap-1 rounded-full border bg-background px-2.5 py-1 text-[11px] hover:bg-muted">
      {Icon && <Icon className="h-3 w-3 text-[#B28A3D]" />} {label}
    </button>
  );
}

function PresentationEntry({ gt, onLaunch }) {
  const { t } = useTranslation();
  const { session, start, resume, unavailable, busy } = gt;
  const label = session?.action_label || t("presentation.action_label");
  const launch = async (fn) => { onLaunch?.(); await fn(); };
  if (unavailable || session?.demo_available === false) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="presentation-unavailable">
        <div className="flex items-center gap-1.5 font-semibold"><MonitorPlay className="h-3.5 w-3.5" /> {label}</div>
        <div className="mt-1">{t("presentation.unavailable")}</div>
      </div>
    );
  }
  const paused = session?.status === "paused" && (session?.step || 0) > 0;
  const completed = session?.status === "completed";
  return (
    <div className="rounded-lg border-2 border-[#17324D]/15 bg-[#17324D]/[0.03] p-3" data-testid="presentation-entry">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#17324D]">
        <MonitorPlay className="h-4 w-4 text-[#B28A3D]" /> {label}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {paused ? (
          <>
            <Button size="sm" onClick={() => launch(resume)} disabled={busy} data-testid="presentation-resume"
              className="bg-[#17324D] hover:bg-[#17324D]/90"><Play className="mr-1 h-3.5 w-3.5" /> {t("presentation.resume")}</Button>
            <Button size="sm" variant="outline" onClick={() => launch(start)} disabled={busy} data-testid="presentation-restart">
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> {t("presentation.restart")}</Button>
          </>
        ) : completed ? (
          <Button size="sm" onClick={() => launch(start)} disabled={busy} data-testid="presentation-replay"
            className="bg-[#17324D] hover:bg-[#17324D]/90"><RotateCcw className="mr-1 h-3.5 w-3.5" /> {t("presentation.replay")}</Button>
        ) : (
          <Button size="sm" onClick={() => launch(start)} disabled={busy} data-testid="presentation-start"
            className="bg-[#17324D] hover:bg-[#17324D]/90"><Play className="mr-1 h-3.5 w-3.5" /> {t("presentation.start")}</Button>
        )}
      </div>
    </div>
  );
}
